import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  HStack,
  Skeleton,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { Heading } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  LIST_FEATURE_FLAGS,
  useFeatureFlag,
} from '@diamond/shared/utils';
import {
  useCartStore,
  useCartTotal,
  useInvalidVoucherHandler,
  useVoucherStore,
} from '@diamond/sol-buyer-context';
import { Check as CheckIcon, ChevronRight } from '@mui/icons-material';
import { useRouter } from 'next/router';
import { ComponentProps, KeyboardEvent, useEffect } from 'react';
import shallow from 'zustand/shallow';

import { CartVoucherModal } from '../cart-voucher-modal';
import { VoucherIcon } from '../Icons';

interface CartTotalData {
  originalPriceMagnify: { magnifiedValue: number };
  finalPriceMagnify: { magnifiedValue: number };
  savedPriceMagnify: { magnifiedValue: number };
  promos: unknown[];
}

// Subcomponents
export const CartSummaryVoucher = ({ onOpen }: { onOpen: () => void }) => {
  const router = useRouter();
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const isVoucherMenuEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_VOUCHERS_MENU,
    true
  );
  const cartItemsCount = useCartStore((state) => state.cartItems.length);
  const appliedVouchers = useVoucherStore(
    (state) => state.appliedVouchers,
    shallow
  );

  if (!isVoucherMenuEnabled) {
    return null;
  }

  const hasProductsSelected = cartItemsCount > 0;
  const hasVouchers = appliedVouchers.length > 0;
  const gradientBg = 'linear(to-r, #FF1E1E, #007AFF)';
  const handleClick = () => {
    if (isMobile) {
      router.push('/voucher');
    } else {
      onOpen();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isMobile) {
        router.push('/voucher');
      } else {
        onOpen();
      }
    }
  };

  let bannerText = 'Gunakan voucher, stok suplai lebih hemat!';

  if (!hasProductsSelected) {
    bannerText = 'Pilih produk untuk lihat voucher';
  } else if (hasVouchers) {
    bannerText = `${appliedVouchers.length} Voucher digunakan`;
  }

  const bannerTextColor = !hasProductsSelected ? '#0000A3' : 'white';
  const iconColor = !hasProductsSelected ? '#0000A3' : 'white';
  const dividerColor = hasProductsSelected
    ? 'linear(to-r, #FF1E1E, #2451CE, #5AC8FF)'
    : '#E5E7EB';

  return (
    <>
      <Box
        w="full"
        bg={!hasProductsSelected ? '#F6F7FB' : undefined}
        bgGradient={hasProductsSelected ? gradientBg : undefined}
        border={!hasProductsSelected ? '1px' : undefined}
        borderColor={!hasProductsSelected ? '#E5E7EB' : undefined}
        px="16px"
        py="10px"
        roundedTop="xl"
        overflow="hidden"
      >
        <Flex
          as="button"
          type="button"
          justifyContent="space-between"
          alignItems="center"
          cursor="pointer"
          bg="transparent"
          border="none"
          w="full"
          h="full"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          {hasVouchers ? (
            <Flex
              alignItems="center"
              justifyContent="center"
              padding="2px"
              bg="white"
              borderRadius="6px"
            >
              <Flex
                w="20px"
                h="20px"
                borderRadius="6px"
                bg="#34C759"
                alignItems="center"
                justifyContent="center"
              >
                <CheckIcon style={{ color: 'white', fontSize: '16px' }} />
              </Flex>
            </Flex>
          ) : (
            <VoucherIcon color={iconColor} boxSize="24px" />
          )}
          <Text
            fontSize="14px"
            color={bannerTextColor}
            flex="1"
            fontWeight="medium"
            ml="8px"
            textAlign="left"
          >
            {bannerText}
          </Text>
          {hasVouchers ? (
            <Flex alignItems="center" gap="4px" color="white">
              <Text fontSize="14px">Ganti</Text>
              <ChevronRight style={{ color: 'white', fontSize: '20px' }} />
            </Flex>
          ) : (
            <ChevronRight
              style={{ color: bannerTextColor, fontSize: '20px' }}
            />
          )}
        </Flex>
      </Box>
      <Box h="4px" bg={dividerColor}></Box>
    </>
  );
};

export const CartSummaryTotal = ({
  cartTotalData,
  isCartTotalFetching,
  onCheckout,
  cartItems,
  isCheckoutLoading = false,
}: {
  cartTotalData: CartTotalData;
  isCartTotalFetching: boolean;
  onCheckout: (cartItems: string[]) => Promise<void>;
  cartItems: string[];
  isCheckoutLoading?: boolean;
}) => {
  const hasItems = cartItems.length > 0;
  const isDisabled =
    !hasItems || !cartTotalData.finalPriceMagnify.magnifiedValue;

  return (
    <CardBody
      pb={{ base: '4', lg: '4' }}
      px={{ base: '4', lg: '4' }}
      pt={{ base: '4', lg: '2' }}
      rounded="2xl"
    >
      <Flex
        flexDir="column"
        gap="3"
        height="auto"
        alignItems="flex-start"
        aria-busy={isCartTotalFetching}
      >
        <Flex
          w="full"
          alignItems="flex-start"
          justifyContent="space-between"
          gap="2"
        >
          <Heading
            fontSize="12px"
            display={{ base: 'block', lg: 'none' }}
            fontWeight="semibold"
          >
            Total Harga
          </Heading>
          <HStack
            spacing="2"
            display={
              cartTotalData.originalPriceMagnify.magnifiedValue !==
              cartTotalData.finalPriceMagnify.magnifiedValue
                ? 'flex'
                : 'none'
            }
          >
            <Text
              textColor="gray.500"
              fontSize={{ base: '14px', lg: '16px' }}
              textDecoration="line-through"
            >
              {buyerCurrencyFormatter(
                cartTotalData.originalPriceMagnify.magnifiedValue
              )}
            </Text>
            <Text textColor="red" fontSize={{ base: '14px', lg: '16px' }}>
              Hemat{' '}
              {buyerCurrencyFormatter(
                cartTotalData.savedPriceMagnify.magnifiedValue
              )}
            </Text>
          </HStack>
        </Flex>
        <Flex gap="2" w="full">
          <Skeleton
            w="full"
            borderRadius="md"
            data-testid="cart-summary-skeleton"
            isLoaded={!isCartTotalFetching}
          >
            <Box
              bg="#FAFAFA"
              rounded="lg"
              p={{ base: '2', lg: '3' }}
              minH={{ sm: '41px', lg: '60px' }}
              display="flex"
              alignItems="center"
            >
              <Text
                fontWeight="bold"
                textColor={isDisabled ? 'gray.500' : '#0000A3'}
                fontSize={{ base: '20px', lg: '22px' }}
              >
                {buyerCurrencyFormatter(
                  hasItems ? cartTotalData.finalPriceMagnify.magnifiedValue : 0
                )}
              </Text>
            </Box>
          </Skeleton>
          <Button
            w="40%"
            minW="116px"
            h="auto"
            minH="41px"
            variant="solid"
            isDisabled={isDisabled}
            isLoading={isCartTotalFetching || isCheckoutLoading}
            onClick={() => onCheckout(cartItems)}
            display={{ base: 'flex', lg: 'none' }}
            alignItems="center"
            justifyContent="center"
          >
            Beli
          </Button>
        </Flex>
        <Button
          w="full"
          minW="116px"
          h="auto"
          minH="41px"
          variant="solid"
          isDisabled={isDisabled}
          isLoading={isCartTotalFetching || isCheckoutLoading}
          onClick={() => onCheckout(cartItems)}
          display={{ base: 'none', lg: 'flex' }}
          alignItems="center"
          justifyContent="center"
        >
          <span>Beli</span>
        </Button>
      </Flex>
    </CardBody>
  );
};

// Main CartSummary component
type CartSummaryProps = {
  handleOrder: (cartItems: string[]) => Promise<void>;
  isCheckoutLoading?: boolean;
  isVoucherModalOpen?: boolean;
  onOpenVoucherModal?: () => void;
  onCloseVoucherModal?: () => void;
} & ComponentProps<typeof Card>;

export const CartSummary = ({
  handleOrder,
  isCheckoutLoading = false,
  isVoucherModalOpen = false,
  onOpenVoucherModal,
  onCloseVoucherModal,
  ...props
}: CartSummaryProps) => {
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const {
    data: cartTotalData,
    isFetching: isCartTotalFetching,
    isError: isCartTotalError,
    error: cartTotalError,
    refetch: refetchCartTotal,
  } = useCartTotal();

  const cartItems = useCartStore((state) => state.cartItems, shallow);

  const cartActions = useCartStore(
    (state) => ({
      setCompiledPromo: state.setCompiledPromo,
      setFreeGoodsPromo: state.setFreeGoodsPromo,
      setDiscountPromo: state.setDiscountPromo,
    }),
    shallow
  );
  const clearVouchers = useVoucherStore((state) => state.clearVouchers);
  useInvalidVoucherHandler(cartTotalData);

  useEffect(() => {
    if (cartItems.length === 0) {
      clearVouchers();
    }
  }, [cartItems.length, clearVouchers]);

  useEffect(() => {
    const shouldDeferStoreUpdate = isCartTotalFetching && cartItems.length > 0;

    if (shouldDeferStoreUpdate) {
      return;
    }

    cartActions.setCompiledPromo(cartTotalData.promos);
    cartActions.setFreeGoodsPromo(cartTotalData.promos);
    cartActions.setDiscountPromo(cartTotalData.promos);
  }, [
    cartTotalData.promos,
    isCartTotalFetching,
    cartItems.length,
    cartActions,
  ]);

  // Simple fallbacks - no need for useMemo here
  const handleOpenVoucher =
    onOpenVoucherModal ||
    (() => {
      /* no-op */
    });
  const handleCloseVoucher =
    onCloseVoucherModal ||
    (() => {
      /* no-op */
    });

  return (
    <>
      <Card
        border="1px"
        borderColor="#F2F2F7"
        {...props}
        shadow="xs"
        borderRadius="14px"
      >
        <CartSummaryVoucher onOpen={handleOpenVoucher} />

        {isCartTotalError && (
          <CardBody pt="4" pb="0" px="4">
            <Alert status="error" rounded="md">
              <AlertIcon />
              <Flex w="full" justifyContent="space-between" alignItems="center">
                <Text fontSize="12px">
                  Gagal memuat total. {String(cartTotalError)}
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => refetchCartTotal()}
                >
                  Coba lagi
                </Button>
              </Flex>
            </Alert>
          </CardBody>
        )}

        <CardHeader
          display={{ base: 'none', lg: 'block' }}
          pb={0}
          pt="4"
          px="4"
        >
          <Heading fontSize="16px" fontWeight="bold">
            Total Harga
          </Heading>
        </CardHeader>

        <CartSummaryTotal
          cartTotalData={cartTotalData}
          isCartTotalFetching={isCartTotalFetching}
          onCheckout={handleOrder}
          cartItems={cartItems}
          isCheckoutLoading={isCheckoutLoading}
        />
      </Card>
      {!isMobile && onOpenVoucherModal && onCloseVoucherModal && (
        <CartVoucherModal
          isOpen={isVoucherModalOpen}
          onClose={handleCloseVoucher}
        />
      )}
    </>
  );
};
